import React from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { colors, fonts } from 'src/components/app-ui'
import LimitReachedModal from 'src/components/limit-reached-modal'
import {
  CREATE_OPTIONS,
  CreateCourseModal,
  useCreateCourseFlow,
} from 'src/components/create-course'

const useStyles = makeStyles(theme => ({
  page: {
    minHeight: '100%',
    background: '#F7F8FA',
    fontFamily: fonts.ui,
    color: colors.navy,
  },
  content: {
    maxWidth: 880,
    margin: '0 auto',
    padding: '32px 40px 60px',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    [theme.breakpoints.down('xs')]: {
      padding: '24px 16px 40px',
    },
  },
  back: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.primary,
  },
  head: {
    marginTop: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    textAlign: 'center',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    letterSpacing: '-.02em',
    color: colors.navy,
  },
  sub: {
    fontSize: '15px',
    color: colors.muted,
  },
  cards: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '20px',
  },
  card: {
    flex: '1 1 280px',
    maxWidth: 340,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '14px',
    padding: '28px 24px 24px',
    border: `1px solid ${colors.border}`,
    borderRadius: '16px',
    background: '#FFFFFF',
    boxShadow: '0 1px 2px rgba(145,158,171,.16)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    '&:hover': {
      borderColor: colors.primary,
      boxShadow: '0 12px 24px -8px rgba(1,95,251,.25)',
    },
  },
  cardIcon: {
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
  },
  cardTitle: {
    fontSize: '17px',
    fontWeight: 600,
    color: colors.navy,
  },
  cardDesc: {
    marginTop: 6,
    fontSize: '14px',
    lineHeight: 1.55,
    color: colors.muted,
  },
  cardFoot: {
    width: '100%',
    marginTop: 'auto',
    paddingTop: 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardMeta: {
    fontSize: '12px',
    color: colors.subtle,
  },
  cardCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.primary,
  },
}))

const isPathId = value =>
  Boolean(value) && value !== 'undefined' && value !== 'null'

/**
 * "How do you want to create your course?" — Create from scratch or AI Course
 * Builder. Opened from Home and older entry points
 * (`/create-course-selection?collectionId=`).
 */
const CreateCourseSelection = () => {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const collectionId = params.get('collectionId')
  const pathId = isPathId(collectionId) ? collectionId : null
  const collaborator = params.get('isCreatedByCurrentUser') === 'false'

  // The path's name is known when coming from its detail page.
  const loadedPath = useSelector(state => state.collection.collection)
  const pathName = pathId && loadedPath?.id === pathId ? loadedPath.name : null

  const flow = useCreateCourseFlow({
    pathId,
    aiSearch: collaborator ? '&isCreatedByCurrentUser=false' : '',
  })

  return (
    <div className={classes.page}>
      <div className={classes.content}>
        <button
          type="button"
          className={classes.back}
          onClick={() => history.goBack()}
        >
          <Icon icon="tabler:chevron-left" width={14} height={14} />
          {pathId ? pathName || 'Learning path' : 'Courses'}
        </button>

        <div className={classes.head}>
          <div className={classes.title}>
            How do you want to create your course?
          </div>
          <div className={classes.sub}>
            {pathId
              ? `The new course is added to ${
                  pathName || 'this learning path'
                }.`
              : 'You can add it to a learning path later.'}
          </div>
        </div>

        <div className={classes.cards}>
          {CREATE_OPTIONS.map(option => (
            <button
              key={option.value}
              type="button"
              className={classes.card}
              onClick={() => flow.select(option.value)}
            >
              <span
                className={classes.cardIcon}
                style={{ background: option.iconBg, color: option.iconColor }}
              >
                <Icon icon={option.icon} width={24} height={24} />
              </span>
              <div>
                <div className={classes.cardTitle}>{option.title}</div>
                <div className={classes.cardDesc}>{option.cardDesc}</div>
              </div>
              <div className={classes.cardFoot}>
                <span className={classes.cardMeta}>{option.meta}</span>
                <span className={classes.cardCta}>
                  Continue
                  <Icon icon="tabler:arrow-right" width={14} height={14} />
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <CreateCourseModal
        open={flow.scratchOpen}
        pathId={pathId}
        pathName={pathName}
        onClose={flow.closeScratch}
        onBack={flow.closeScratch}
      />
      {flow.limitModalOpen && (
        <LimitReachedModal
          isModalOpen={flow.limitModalOpen}
          handleCloseModal={flow.closeLimitModal}
        />
      )}
    </div>
  )
}

export default CreateCourseSelection
